import React from 'react';
import { getPersonalityTheme } from './theme-config';
import './HeroCard.css';

/**
 * HeroCard — The compact, shareable "viral unit"
 *
 * Cherry-picks the most impactful data from all 3 cards:
 * - Persona icon + archetype title (from personality_card)
 * - Brain balance bar (from personality_card)
 * - Top 3 strengths (from personality_card.coreStrengths)
 * - Career DNA pills (from growth_card.careerAlignment) — age-adapted
 * - One-liner tagline (from personality_card)
 *
 * Designed to be screenshot-ready for social sharing.
 */

const HeroCard = ({ personalityData, growthData, onShare, className = '' }) => {
  if (!personalityData) return null;

  const theme = getPersonalityTheme(personalityData.persona);

  const strengths = (personalityData.coreStrengths || []).slice(0, 3);
  const brainBalance = personalityData.brainBalance || { leftPercent: 50, rightPercent: 50 };

  // Superpower: use thinking style primary or first core strength
  const superpower = personalityData.thinkingStyle?.primary
    || (strengths[0]?.label)
    || 'Unique Perspective';

  return (
    <div className={`hero-card ${className}`} id="hero-card-capture">
      {/* Decorative glow */}
      <div className="hero-card-glow" />

      {/* Quick Share Button */}
      {onShare && (
        <button 
          onClick={onShare}
          className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-white/5 hover:bg-white/15 border border-white/10 flex items-center justify-center transition-all shadow-[0_4px_12px_rgba(0,0,0,0.3)] backdrop-blur-md text-white/70 hover:text-white"
          title="Share your results"
        >
          <svg className="w-[16px] h-[16px]" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
            <circle cx="18" cy="5" r="3"></circle>
            <circle cx="6" cy="12" r="3"></circle>
            <circle cx="18" cy="19" r="3"></circle>
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
          </svg>
        </button>
      )}

      <div className="hero-card-inner">
        {/* Header: Icon + Name + Archetype */}
        <div className="hero-header">
          <div
            className="hero-persona-icon"
            style={{
              background: `linear-gradient(135deg, ${theme.primary}33, ${theme.secondary}22)`,
              borderColor: `${theme.primary}66`,
              boxShadow: `0 0 18px ${theme.primary}55, 0 0 6px ${theme.primary}33`
            }}
          >
            {theme.icon}
          </div>
          <div className="hero-name-block">
            <h2 className="hero-name">{personalityData.name}</h2>
            <div className="hero-archetype" style={{ color: theme.accent }}>
              {personalityData.brainType || theme.name}
            </div>
          </div>
        </div>

        {/* One-liner tagline */}
        {personalityData.oneLiner && (
          <div className="hero-tagline">
            "{personalityData.oneLiner}"
          </div>
        )}

        {/* Brain Balance */}
        <div className="hero-brain-section">
          <div className="hero-brain-labels">
            <div className="hero-brain-side">
              <span className="hero-brain-percent left">
                {Math.round(brainBalance.leftPercent)}%
              </span>
              <span className="hero-brain-label">Logic</span>
            </div>
            <span className="hero-brain-divider">⚡</span>
            <div className="hero-brain-side">
              <span className="hero-brain-percent right">
                {Math.round(brainBalance.rightPercent)}%
              </span>
              <span className="hero-brain-label">Creativity</span>
            </div>
          </div>
          <div className="hero-brain-bar">
            <div
              className="hero-brain-bar-left"
              style={{ width: `${brainBalance.leftPercent}%` }}
            />
            <div
              className="hero-brain-bar-right"
              style={{ width: `${brainBalance.rightPercent}%` }}
            />
          </div>
        </div>

        {/* Strengths & Growth Areas */}
        {(strengths.length > 0 || growthData?.growthOpportunities?.length > 0) && (
          <div className="flex gap-4">
            {strengths.length > 0 && (
              <div className="hero-strengths flex-1 min-w-0">
                <div className="hero-strengths-title">Core Strengths</div>
                <div className="hero-strength-row">
                  {strengths.slice(0, 2).map((s, i) => (
                    <div key={i} className="hero-strength-pill w-full overflow-hidden">
                      <span className="hero-strength-icon">{s.icon}</span>
                      <span className="hero-strength-label" style={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: '1.3', fontSize: '0.72rem' }}>{s.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {growthData?.growthOpportunities?.[0]?.area && (
               <div className="hero-strengths flex-1 min-w-0">
                 <div className="hero-strengths-title text-pink-400">Your Weaknesses</div>
                 <div className="hero-strength-row">
                   {growthData.growthOpportunities.slice(0, 2).map((opp, i) => (
                     <div key={i} className="hero-strength-pill w-full !border-pink-500/30 !bg-pink-500/10 overflow-hidden">
                       <span className="hero-strength-icon">🎯</span>
                       <span className="hero-strength-label text-pink-200" style={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: '1.3', fontSize: '0.72rem' }}>{opp.area}</span>
                     </div>
                   ))}
                 </div>
               </div>
            )}
          </div>
        )}

        {/* Your Superpower — shareable identity hook */}
        <div className="hero-superpower">
          <div className="hero-superpower-label">⚡ YOUR SUPERPOWER</div>
          <div className="hero-superpower-value">{superpower}</div>
        </div>

        {/* Viral Quote — the scroll-hook that makes users feel deeply seen */}
        {(() => {
          const viralQuote = personalityData.viralQuote;
          const fallbackInsight = personalityData.keyInsights?.[0];
          const fallbackText = typeof fallbackInsight === 'object'
            ? (fallbackInsight?.description || fallbackInsight?.insight || '')
            : (typeof fallbackInsight === 'string' ? fallbackInsight : '');
          const displayText = viralQuote || fallbackText || growthData?.motivation?.personalizedMessage || '';
          return displayText ? (
            <div className="hero-insight">
              <span className="hero-insight-text">"{displayText}"</span>
            </div>
          ) : null;
        })()}

        {/* Footer watermark */}
        <div className="hero-footer">
          <span className="hero-watermark">youdontknowyou.com</span>
          <span className="hero-watermark-icon">🧠</span>
        </div>
      </div>
    </div>
  );
};

export default HeroCard;
